import React from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { CampaignScheduleImportCandidatesResponse } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

import CampaignScheduleImportCandidateRow from './CampaignScheduleImportCandidateRow';
import ImportBlockersList from './ImportBlockersList';
import { useCampaignScheduleImportSelection } from './useCampaignScheduleImportSelection';

type Props = {
  error?: unknown;
  importCandidates?: CampaignScheduleImportCandidatesResponse;
  isError: boolean;
  isLoading: boolean;
  offerId?: string;
  onClose: () => void;
  open: boolean;
};

export default function SalesforceCampaignSchedulesModal({
  error,
  importCandidates,
  isError,
  isLoading,
  offerId,
  onClose,
  open,
}: Props) {
  const {
    allSelected,
    allWarnings,
    candidateGroups,
    candidates,
    conflictingTripleKeys,
    handleImport,
    importBlockers,
    importMutation,
    liveBlockers,
    resultByKey,
    selectableCandidates,
    selectedKeys,
    skippedKeys,
    someSelected,
    toggleKeys,
    toggleSelectAll,
  } = useCampaignScheduleImportSelection(offerId, importCandidates, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Schedules from Salesforce</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load Salesforce campaign schedules'}
          </Alert>
        )}

        {!isLoading && !isError && importCandidates && (
          <Stack spacing={3}>
            {!importMutation.isError && liveBlockers?.length ? (
              <Alert severity="error">
                <Typography fontWeight={700} gutterBottom variant="body2">
                  Import blocked:
                </Typography>
                <ImportBlockersList blockers={liveBlockers} />
              </Alert>
            ) : null}

            {importMutation.isError && (
              <Alert severity="error">
                <Typography fontWeight={700} gutterBottom variant="body2">
                  {importBlockers?.length ? 'Import blocked:' : 'Import failed'}
                </Typography>
                {importBlockers?.length ? (
                  <ImportBlockersList blockers={importBlockers} />
                ) : (
                  <Typography variant="body2">
                    {importMutation.error instanceof Error ? importMutation.error.message : 'Please try again.'}
                  </Typography>
                )}
              </Alert>
            )}

            {skippedKeys.length > 0 && (
              <Alert severity="warning">
                {skippedKeys.length} selected schedule{skippedKeys.length === 1 ? '' : 's'} could no longer be found
                (Salesforce or this offer's schedules changed) and {skippedKeys.length === 1 ? 'was' : 'were'} skipped.
              </Alert>
            )}

            {allWarnings.length > 0 && (
              <Stack spacing={1}>
                {allWarnings.map((warning) => (
                  <Alert key={warning} severity="warning" sx={{ whiteSpace: 'pre-line' }}>
                    {warning}
                  </Alert>
                ))}
              </Stack>
            )}

            <Box>
              <Typography variant="h6" gutterBottom>
                Schedules to import ({candidates.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        disabled={selectableCandidates.length === 0 || importMutation.isPending}
                        indeterminate={someSelected}
                        inputProps={{ 'aria-label': 'Select all importable schedules' }}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Previous Dates</TableCell>
                    <TableCell>Target Dates</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidateGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>No importable schedules derived from Salesforce for this offer</TableCell>
                    </TableRow>
                  ) : (
                    candidateGroups.map((group) => {
                      const keys = group.candidates.map((candidate) => candidate.key);
                      const groupChecked = keys.every((key) => selectedKeys.has(key));
                      const isConflicting = group.candidates.some((candidate) =>
                        conflictingTripleKeys.has(`${candidate.type}::${candidate.brand}::${candidate.region}`),
                      );

                      return (
                        <CampaignScheduleImportCandidateRow
                          key={group.key}
                          group={group}
                          isChecked={groupChecked}
                          isConflicting={isConflicting}
                          isIndeterminate={!groupChecked && keys.some((key) => selectedKeys.has(key))}
                          isSelectionDisabled={importMutation.isPending}
                          result={resultByKey.get(group.candidates[0].key)}
                          onToggle={() => toggleKeys(keys, !groupChecked)}
                        />
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          disabled={selectedKeys.size === 0 || importMutation.isPending || !offerId || !!liveBlockers?.length}
          variant="contained"
          onClick={handleImport}
        >
          {importMutation.isPending ? 'Importing…' : `Import selected (${selectedKeys.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
